/**
 * <Jargon term="mrr"/> — renders the plain-English label + hover tooltip in
 * Basic mode, or the raw technical term (undecorated, matching the pre-
 * directive UI exactly) in Advanced mode. See lib/adminGlossary.js for terms.
 *
 * <JargonToggle/> — the Basic/Advanced pill switch, mounted once in
 * AdminHeader.jsx so it applies to every /admin/* page.
 */
import { Info } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useJargon } from "@/context/JargonContext";
import { ADMIN_GLOSSARY } from "@/lib/adminGlossary";

export function Jargon({ term, className = "" }) {
    const ctx = useJargon();
    const level = ctx?.level ?? "basic";
    const g = ADMIN_GLOSSARY[term];
    if (!g) return null;

    if (level === "advanced") {
        return <span className={className}>{g.technical}</span>;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className={`inline-flex items-center gap-0.5 cursor-help border-b border-dotted border-muted-foreground/50 ${className}`}
                    data-testid={`jargon-${term}`}
                >
                    {g.basic}
                    <Info className="w-3 h-3 opacity-60 shrink-0" />
                </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs" data-testid={`jargon-tooltip-${term}`}>
                <b>{g.technical}</b> — {g.definition}
            </TooltipContent>
        </Tooltip>
    );
}

const LEVELS = [
    { id: "basic", label: "Basic" },
    { id: "advanced", label: "Advanced" },
];

export function JargonToggle() {
    const { level, setLevel } = useJargon();
    return (
        <div
            className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 p-1"
            data-testid="admin-jargon-toggle"
            title="Plain-English vs technical labels across the admin dashboard"
        >
            {LEVELS.map((l) => (
                <button
                    key={l.id}
                    type="button"
                    onClick={() => setLevel(l.id)}
                    data-testid={`admin-jargon-${l.id}`}
                    data-selected={level === l.id}
                    className={
                        "px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors " +
                        (level === l.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground")
                    }
                >
                    {l.label}
                </button>
            ))}
        </div>
    );
}
